import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';

// =====================================================================
// PLENARIUM:LEITURAS -- na TV, o que o Secretario esta lendo agora
// (ata anterior, item do Expediente ou materia da Ordem do Dia).
// Some sozinho durante uma votacao aberta (os votos tem prioridade) e
// quando o Secretario ou o admin tiram da TV.
// PLENARIUM:LEITURA_TV_VISUAL -- visual de painel oficial: faixa do topo
// com a camara e o relogio, cartao central e rodape com quem le e ha
// quanto tempo. PLENARIUM:LEITURA_TV_BRASAO -- brasao da camara no topo
// e em marca d'agua no cartao (prop brasao, vinda do PainelTV).
// =====================================================================

const CABECALHO = { EXPEDIENTE: 'LEITURA DO EXPEDIENTE', ATA: 'LEITURA DA ATA DA SESSÃO ANTERIOR', MATERIA: 'LEITURA DA MATÉRIA' };
const SUBTITULO = { EXPEDIENTE: 'Expediente', ATA: 'Para apreciação do Plenário', MATERIA: 'Ordem do Dia' };
const COR = { EXPEDIENTE: '#fbbf24', ATA: '#5eead4', MATERIA: '#93c5fd' };

const p2 = (n) => String(n).padStart(2, '0');
const mesmo = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const iniciais = (nome) => String(nome || '').replace(/^\s*\d+[\u00ba\u00b0\u00aa]?\s*/, '').split(/\s+/)
  .filter(p => p && !/^(de|da|do|das|dos|e|secret[a\u00e1]ri[oa]|presidente|vereador[a]?)$/i.test(p))
  .slice(0, 2).map(p => p[0].toUpperCase()).join('');

function IconeLeitura({ tipo, cor, tam }) {
  const st = { width: tam, height: tam, display: 'block' };
  if (tipo === 'ATA') return (
    <svg viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={st}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h6M9 9h2" />
    </svg>
  );
  if (tipo === 'MATERIA') return (
    <svg viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={st}>
      <path d="M12 3v18" /><path d="M5 7h14" /><path d="M5 7l-3 7a3.5 3.5 0 0 0 6 0z" /><path d="M19 7l-3 7a3.5 3.5 0 0 0 6 0z" /><path d="M8 21h8" />
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={st}>
      <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" />
    </svg>
  );
}

export default function LeituraTV({ sessaoId, oculto = false, camaraNome = '', headers = {}, brasao = '' }) {
  const [semBrasao, setSemBrasao] = useState(false); // PLENARIUM:LEITURA_TV_BRASAO -- imagem quebrada: some
  const [leitura, setLeitura] = useState(null);
  const [agora, setAgora] = useState(() => new Date());
  const auth = headers && headers.Authorization ? headers.Authorization : '';

  useEffect(() => {
    if (!sessaoId) { setLeitura(null); return undefined; }
    const buscar = () => axios.get(`/api/leituras/sessao/${sessaoId}/atual`, { headers: auth ? { Authorization: auth } : {} }).then(r => setLeitura(r.data || null)).catch(() => {});
    buscar();
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    socket.on('connect', () => { socket.emit('entrar-sala', `sessao-${sessaoId}`); buscar(); });
    socket.on('leitura-atual', (d) => { if (d && d.sessaoId === sessaoId) setLeitura(d.leitura || null); });
    return () => socket.disconnect();
  }, [sessaoId, auth]);

  const visivel = !!leitura && !oculto;
  useEffect(() => {
    if (!visivel) return undefined;
    const t = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(t);
  }, [visivel]);

  if (!visivel) return null;
  const cor = COR[leitura.tipo] || '#fbbf24';
  const titulo = leitura.titulo || '';
  const longo = titulo.length > 220;
  const medio = titulo.length > 90;
  const rotulo = leitura.rotulo && !mesmo(leitura.rotulo, titulo) ? leitura.rotulo : (!mesmo(SUBTITULO[leitura.tipo], titulo) ? (SUBTITULO[leitura.tipo] || '') : '');
  const desde = leitura.desde ? new Date(leitura.desde) : null;
  const seg = desde && !isNaN(desde) ? Math.max(0, Math.floor((agora - desde) / 1000)) : null;
  const tempo = seg === null ? null : (seg >= 3600 ? `${Math.floor(seg / 3600)}:${p2(Math.floor(seg / 60) % 60)}:${p2(seg % 60)}` : `${p2(Math.floor(seg / 60))}:${p2(seg % 60)}`);
  const ini = iniciais(leitura.leitor);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex', flexDirection: 'column', color: '#fff', background: 'radial-gradient(80% 60% at 12% 0%, rgba(37,99,235,.28) 0%, transparent 60%), radial-gradient(60% 50% at 100% 100%, rgba(20,184,166,.10) 0%, transparent 60%), linear-gradient(160deg, #050f29 0%, #0a1d46 55%, #061330 100%)', boxSizing: 'border-box', overflow: 'hidden' }}>
      <style>{'@keyframes plnLeituraPulso{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(.8)}}'}</style>

      {/* faixa do topo: brasao e nome da camara | tipo da leitura e relogio */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '3vw', padding: '2.6vh 5vw', background: 'rgba(2,8,23,.45)', borderBottom: '0.2vh solid rgba(255,255,255,.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2vh', minWidth: 0 }}>
          {brasao && !semBrasao && <img src={brasao} alt="Brasão" onError={() => setSemBrasao(true)} style={{ height: '8.5vh', width: 'auto', maxWidth: '12vh', objectFit: 'contain', filter: 'drop-shadow(0 0.6vh 1.6vh rgba(0,0,0,.45))', flex: '0 0 auto' }} />}
          {camaraNome && (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '2.6vh', fontWeight: 900, letterSpacing: '0.2vh', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{camaraNome}</div>
              <div style={{ fontSize: '1.6vh', fontWeight: 700, color: 'rgba(255,255,255,.5)', letterSpacing: '0.45vh', marginTop: '0.3vh' }}>PODER LEGISLATIVO</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2.4vw', flex: '0 0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '1.3vh', padding: '1.1vh 2.4vh', borderRadius: 999, background: `${cor}14`, border: `0.25vh solid ${cor}66` }}>
            <span style={{ width: '1.4vh', height: '1.4vh', borderRadius: '50%', background: cor, boxShadow: `0 0 2vh ${cor}`, animation: 'plnLeituraPulso 1.6s ease-in-out infinite' }} />
            <span style={{ fontSize: '2.1vh', fontWeight: 900, letterSpacing: '0.3vh', color: cor, whiteSpace: 'nowrap' }}>{CABECALHO[leitura.tipo] || 'LEITURA'}</span>
          </div>
          <div style={{ width: '0.2vh', height: '4vh', background: 'rgba(255,255,255,.15)' }} />
          <div style={{ fontSize: '3.6vh', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.2vh', color: '#fff' }}>{p2(agora.getHours())}:{p2(agora.getMinutes())}</div>
        </div>
      </div>

      {/* cartao central */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', padding: '4vh 5vw' }}>
        <div style={{ position: 'relative', width: '100%', maxHeight: '100%', display: 'flex', gap: '4vw', alignItems: 'center', background: 'linear-gradient(135deg, rgba(255,255,255,.07) 0%, rgba(255,255,255,.025) 100%)', border: '0.2vh solid rgba(255,255,255,.10)', borderRadius: '2.4vh', padding: '6vh 4.5vw', boxSizing: 'border-box', boxShadow: '0 4vh 10vh rgba(0,0,0,.35)', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0.8vh', background: `linear-gradient(180deg, ${cor} 0%, ${cor}55 100%)` }} />
          {brasao && !semBrasao && <img src={brasao} alt="" aria-hidden="true" style={{ position: 'absolute', right: '-3vh', top: '50%', transform: 'translateY(-50%)', height: '130%', maxHeight: '60vh', width: 'auto', objectFit: 'contain', opacity: 0.05, filter: 'grayscale(100%) brightness(2)', pointerEvents: 'none' }} />}
          <div style={{ position: 'relative', flex: '0 0 auto', width: '16vh', height: '16vh', borderRadius: '3vh', background: `${cor}14`, border: `0.25vh solid ${cor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconeLeitura tipo={leitura.tipo} cor={cor} tam="9vh" />
          </div>
          <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2.4vh' }}>
            {rotulo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.4vh' }}>
                <span style={{ width: '5vh', height: '0.35vh', borderRadius: 9, background: cor }} />
                <span style={{ fontSize: '2.5vh', fontWeight: 800, color: cor, textTransform: 'uppercase', letterSpacing: '0.35vh' }}>{rotulo}</span>
              </div>
            )}
            <div style={{ fontSize: longo ? '4.4vh' : medio ? '5.4vh' : '7vh', fontWeight: 900, lineHeight: 1.18, letterSpacing: '-0.05vh', textAlign: longo ? 'justify' : 'left', textAlignLast: 'left', display: '-webkit-box', WebkitLineClamp: longo ? 8 : 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{titulo}</div>
            {leitura.texto && (
              <div style={{ fontSize: '2.9vh', lineHeight: 1.5, color: 'rgba(255,255,255,.80)', textAlign: 'justify', paddingTop: '2.4vh', borderTop: '0.2vh solid rgba(255,255,255,.10)', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{leitura.texto}</div>
            )}
          </div>
        </div>
      </div>

      {/* rodape: quem le e ha quanto tempo */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '3vw', padding: '2.6vh 5vw 3.4vh', borderTop: '0.2vh solid rgba(255,255,255,.08)', background: 'rgba(2,8,23,.35)' }}>
        {leitura.leitor ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.8vh', minWidth: 0 }}>
            <div style={{ flex: '0 0 auto', width: '6.4vh', height: '6.4vh', borderRadius: '50%', background: `linear-gradient(135deg, ${cor} 0%, ${cor}99 100%)`, color: '#06122e', fontWeight: 900, fontSize: '2.5vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{ini || '•'}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '1.7vh', fontWeight: 800, color: 'rgba(255,255,255,.5)', letterSpacing: '0.3vh' }}>EM LEITURA</div>
              <div style={{ fontSize: '3vh', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{leitura.leitor}</div>
            </div>
          </div>
        ) : <span />}
        {tempo && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.7vh', fontWeight: 800, color: 'rgba(255,255,255,.5)', letterSpacing: '0.3vh' }}>TEMPO DE LEITURA</div>
            <div style={{ fontSize: '3.4vh', fontWeight: 800, fontFamily: 'monospace', color: cor }}>{tempo}</div>
          </div>
        )}
      </div>
    </div>
  );
}
